import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterLink } from '@angular/router';

import { ChangeDetectionStrategy } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from 'ui-library';
import { Pop } from 'src/app/classes/shared/pop';
import { AuthService } from 'src/app/services/core/auth.service';

@Component({
  selector: 'app-login',
  imports: [
    ButtonComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    RouterLink,
    MatSelectModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss'
})
export class ContactUsComponent {
  private readonly auth: AuthService = inject(AuthService);
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);

  name: string = '';
  email: string = '';
  object: string = '';
  message: string = '';
  submitted = false;
  isSubmitting = false;

  // Only ever asked for/used when object === 'account_creation' - there is no dedicated
  // self-service signup flow, so this reuses the same contactUs() support-ticket route as
  // every other subject, folding these into the message body (buildAccountCreationMessage())
  // rather than the backend's own contactUs() (unifield-api-v3's middleware/public.js), which
  // only ever reads email/name/object/message and would otherwise silently drop everything
  // else. Mirrors unifield-mobile-2's contact-us.ts exactly.
  firstName: string = '';
  lastName: string = '';
  password: string = '';
  position: string = '';
  company: string = '';
  projectName: string = '';

  // Same password strength rules already enforced on reset-password.page.ts - kept in sync
  // with it rather than invented separately, so a password accepted here is guaranteed to
  // also be accepted once the account actually gets created and the user later resets/changes it.
  passwordCriteria = {
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    noSpaces: false
  };

  get isAccountCreation(): boolean {
    return this.object === 'account_creation';
  }

  get isPasswordValid(): boolean {
    const c = this.passwordCriteria;
    return c.length && c.uppercase && c.lowercase && c.digit && c.noSpaces;
  }

  checkPasswordCriteria(): void {
    const password = this.password || '';
    this.passwordCriteria.length = password.length >= 6;
    this.passwordCriteria.uppercase = /[A-Z]/.test(password);
    this.passwordCriteria.lowercase = /[a-z]/.test(password);
    this.passwordCriteria.digit = /\d/.test(password);
    this.passwordCriteria.noSpaces = password.length > 0 && !/\s/.test(password);
  }

  private buildAccountCreationMessage(): string {
    const lines = [
      `${this.translate.instant('contact_us.first_name')} : ${this.firstName}`,
      `${this.translate.instant('contact_us.last_name')} : ${this.lastName}`,
      `${this.translate.instant('login.email')} : ${this.email}`,
      `${this.translate.instant('contact_us.password')} : ${this.password}`,
      `${this.translate.instant('contact_us.position')} : ${this.position}`,
      `${this.translate.instant('contact_us.company')} : ${this.company}`,
      `${this.translate.instant('contact_us.project_name')} : ${this.projectName}`,
    ];
    if (this.message?.trim()) lines.push('', this.message.trim());
    return lines.join('\n');
  }

  onSubmit(form: NgForm): void {
    this.submitted = true;
    if (!form.valid) return;
    if (this.isAccountCreation && !this.isPasswordValid) return;

    this.isSubmitting = true;

    if (this.isAccountCreation) this.name = `${this.firstName} ${this.lastName}`.trim();

    this.auth.contactUs({
      name: this.name,
      email: this.email.toLowerCase(),
      object: this.object,
      message: this.isAccountCreation ? this.buildAccountCreationMessage() : this.message
    }).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.pop.toast(this.translate.instant('contact_us.success'), 'success', 5000, 'send');
        this.resetFields();
      },
      error: () => {
        this.isSubmitting = false;
        this.pop.error(this.translate.instant('contact_us.error'));
      }
    });
  }

  resetFields() {
    this.submitted = false;
    this.name = '';
    this.email = '';
    this.object = '';
    this.message = '';
    this.firstName = '';
    this.lastName = '';
    this.password = '';
    this.position = '';
    this.company = '';
    this.projectName = '';
    this.passwordCriteria = { length: false, uppercase: false, lowercase: false, digit: false, noSpaces: false };
  }
}
